/**
 * DURABLE OFFLINE ORDER STORE — IndexedDB, with a localStorage backup.
 *
 * PRODUCT CHANGE §2 (`DEVIATIONS.md`). Nothing in the original storefront
 * queues an order; this whole file is new, and it is modelled directly on the
 * recovered POS's queue (`pos-recovered/src/app/persistStorage.js`,
 * `offlineOrdersPersistMiddleware.js`, `utils/storagePersistence.js`) because
 * that queue has already been through the failure this one has to survive.
 *
 * ## The failure it is built around
 *
 * On the native host, **the browser evicted IndexedDB for real**. A session's
 * queue disappeared and the symptom the shop saw was "the app doesn't run" —
 * not "an order was lost", which is the dangerous part: silent loss reads as a
 * crash and gets nobody looking for the orders.
 *
 * Three defences, all of them the POS's:
 *
 * 1. **Ask for persistent storage.** `navigator.storage.persist()` moves the
 *    origin out of "best effort" and into a bucket the browser will not clear
 *    under pressure. It is a REQUEST — Chrome grants it on its own heuristics
 *    (installed PWA, engagement, bookmark) and can simply say no, which is why
 *    the answer is recorded and surfaced rather than assumed.
 * 2. **A second copy in localStorage.** A different storage bucket with a
 *    different eviction policy. `readAll()` merges it back in whenever
 *    IndexedDB comes up empty or unreadable, and says so in the console.
 * 3. **A sentinel that does NOT live in IndexedDB.** `hasEverPersisted` is in
 *    localStorage on purpose — the POS's note is exact: "a sentinel stored
 *    beside the thing it vouches for would be evicted with it". Without it a
 *    first-ever boot is indistinguishable from an eviction.
 *
 * ## What this module does NOT promise
 *
 * It cannot make a browser keep data. Clearing site data, a private window
 * closing, "clear cookies and site data on exit", or a browser that refuses
 * persistence and then comes under real storage pressure will all take the
 * queue with them. `describeDurability()` returns exactly that, in words, for
 * the UI to show — see `OfflineOrdersBanner`. Promising more than this is the
 * one thing that must not happen here.
 */

const DB_NAME = "eposmatic-web-offline";
const DB_VERSION = 1;
const STORE_NAME = "orders";

/**
 * The backup key. `ALWAYS_` prefix, and the shape, are the POS's own
 * (`pos-recovered/src/constants/enums.js` — `ALWAYS_offline_orders_backup`);
 * the `_web` suffix keeps this app's copy separate from a POS running on the
 * same host, which would otherwise share the origin's localStorage.
 */
const BACKUP_KEY = "ALWAYS_offline_orders_backup_web";
const PERSIST_SENTINEL_KEY = "ALWAYS_offline_orders_persisted_web";

/** A queued order, exactly as it will be posted to `offline-sync`. */
export interface QueuedOrder {
  /**
   * The row's key AND the `_id` the server upserts on. A 24-hex ObjectId
   * minted here — see `mintObjectId` in `offlineOrderDocument.ts` for why this
   * cannot be a UUID.
   */
  _id: string;
  /** A `crypto.randomUUID()`, for local bookkeeping and for the UI's key. */
  local_uuid: string;
  tenant_id: string;
  /** `Date.now()` when the customer pressed the button. */
  queued_at: number;
  /** How many sync attempts this row has survived. */
  attempts: number;
  /** The last error the server or the network gave, for the UI. */
  last_error?: string | null;
  /** Snapshot of the Console gate at the moment this order was queued. */
  lan_relay_enabled?: boolean;
  /** What the customer was SHOWN, so the banner can name the order. */
  summary: {
    order_type: string;
    item_count: number;
    /** Cents, and the same figure as `document.grand_total`. */
    grand_total: number;
    currency_symbol: string;
  };
  /** The full order document. */
  document: Record<string, unknown>;
}

/* -------------------------------------------------------------------------- */
/* IndexedDB                                                                  */
/* -------------------------------------------------------------------------- */

const hasIndexedDb = (): boolean =>
  typeof window !== "undefined" && typeof window.indexedDB !== "undefined";

let dbPromise: Promise<IDBDatabase | null> | null = null;

const openDb = (): Promise<IDBDatabase | null> => {
  if (!hasIndexedDb()) return Promise.resolve(null);
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase | null>((resolve) => {
    let request: IDBOpenDBRequest;
    try {
      request = window.indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "_id" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    // A private window, a disabled-storage policy or a corrupt database all
    // land here. `null` means "fall back to the backup copy", never a throw:
    // an exception on this path would take the checkout down with it.
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });
  return dbPromise;
};

const idbAll = async (): Promise<QueuedOrder[] | null> => {
  const db = await openDb();
  if (!db) return null;
  return new Promise<QueuedOrder[] | null>((resolve) => {
    try {
      const tx = db.transaction(STORE_NAME, "readonly");
      const request = tx.objectStore(STORE_NAME).getAll();
      request.onsuccess = () => resolve((request.result ?? []) as QueuedOrder[]);
      request.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
};

const idbWrite = async (
  work: (store: IDBObjectStore) => void,
): Promise<boolean> => {
  const db = await openDb();
  if (!db) return false;
  return new Promise<boolean>((resolve) => {
    try {
      const tx = db.transaction(STORE_NAME, "readwrite");
      work(tx.objectStore(STORE_NAME));
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
      tx.onabort = () => resolve(false);
    } catch {
      resolve(false);
    }
  });
};

/* -------------------------------------------------------------------------- */
/* localStorage backup                                                        */
/* -------------------------------------------------------------------------- */

const readBackup = (): QueuedOrder[] => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(BACKUP_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    const rows = Array.isArray(parsed) ? parsed : parsed?.data;
    if (!Array.isArray(rows)) return [];
    // The POS discards a record without a string `_id` rather than storing it
    // (`isValidOfflineOrder`); a row that cannot be keyed cannot be synced or
    // cleared, so keeping it would only ever grow the queue.
    return rows.filter(
      (row): row is QueuedOrder =>
        row != null && typeof row === "object" && typeof row._id === "string",
    );
  } catch {
    return [];
  }
};

const writeBackup = (rows: QueuedOrder[]): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(BACKUP_KEY, JSON.stringify({ data: rows }));
  } catch {
    // A full or disabled localStorage must not fail the write that IndexedDB
    // already accepted. The queue is one copy down, and `describeDurability`
    // reports that.
  }
};

/* -------------------------------------------------------------------------- */
/* persistence request                                                        */
/* -------------------------------------------------------------------------- */

export interface DurabilityReport {
  /** The browser granted persistent storage for this origin. */
  persisted: boolean;
  /** `navigator.storage.persist` exists at all (it needs a secure context). */
  supported: boolean;
  /** A backup copy is being kept in localStorage. */
  backedUp: boolean;
  /** True when a previous session was persisted and this one is not. */
  lostPersistence: boolean;
  /** One sentence, plain words, safe to show a customer or an operator. */
  message: string;
}

/**
 * Ask for persistent storage, once per load, and describe honestly what came
 * back. Called by `OfflineOrdersBridge` at boot.
 */
export const requestDurableStorage = async (): Promise<DurabilityReport> => {
  const supported =
    typeof navigator !== "undefined" &&
    typeof navigator.storage?.persist === "function";

  let persisted = false;
  if (supported) {
    try {
      persisted =
        (await navigator.storage.persisted?.()) ||
        (await navigator.storage.persist());
    } catch {
      persisted = false;
    }
  }

  let everPersisted = false;
  try {
    everPersisted =
      typeof window !== "undefined" &&
      window.localStorage.getItem(PERSIST_SENTINEL_KEY) === "1";
    if (persisted) window.localStorage.setItem(PERSIST_SENTINEL_KEY, "1");
  } catch {
    everPersisted = false;
  }

  const backedUp = typeof window !== "undefined" && (() => {
    try {
      const probe = "__eposmatic_probe__";
      window.localStorage.setItem(probe, "1");
      window.localStorage.removeItem(probe);
      return true;
    } catch {
      return false;
    }
  })();

  const lostPersistence = everPersisted && !persisted;

  const message = persisted
    ? "Orders taken offline are saved on this device and the browser has agreed not to clear them. They are sent as soon as the connection is back."
    : backedUp
      ? "Orders taken offline are saved on this device in two places and sent as soon as the connection is back. The browser has not guaranteed to keep them, so clearing site data — or closing a private window — would lose anything not yet sent."
      : "Orders taken offline are saved on this device only, and this browser has not guaranteed to keep them. Do not clear site data, and reconnect as soon as you can.";

  return { persisted, supported, backedUp, lostPersistence, message };
};

/* -------------------------------------------------------------------------- */
/* the queue                                                                  */
/* -------------------------------------------------------------------------- */

const byQueuedAt = (a: QueuedOrder, b: QueuedOrder): number =>
  a.queued_at - b.queued_at;

/**
 * Every queued order, newest last.
 *
 * IndexedDB is the primary. When it answers `null` (unreadable) or an EMPTY
 * list while the backup has rows, the backup wins and is written back — that
 * is the eviction case, and it is logged with the same wording the POS uses so
 * the two are searchable together.
 */
export const readAll = async (): Promise<QueuedOrder[]> => {
  const primary = await idbAll();
  const backup = readBackup();

  if (primary == null || (primary.length === 0 && backup.length > 0)) {
    if (backup.length > 0) {
      // eslint-disable-next-line no-console
      console.warn(
        `[OfflineOrders] Storage evicted — recovered ${backup.length} order(s) from localStorage backup`,
      );
      await idbWrite((store) => backup.forEach((row) => store.put(row)));
    }
    return [...backup].sort(byQueuedAt);
  }

  // Union, not replace: a row present in only one of the two copies is still a
  // real order. Keys are the ObjectIds, so a row in both is one row.
  const merged = new Map<string, QueuedOrder>();
  backup.forEach((row) => merged.set(row._id, row));
  primary.forEach((row) => merged.set(row._id, row));
  const rows = [...merged.values()].sort(byQueuedAt);
  if (rows.length !== backup.length) writeBackup(rows);
  return rows;
};

/** Add one order. Written to BOTH copies before the caller is told it landed. */
export const enqueue = async (order: QueuedOrder): Promise<QueuedOrder[]> => {
  const existing = await readAll();
  // Keyed on the ObjectId, so a double-tapped Place Order button — or a retry
  // of this very function — replaces the row rather than adding a second.
  const next = [...existing.filter((row) => row._id !== order._id), order].sort(
    byQueuedAt,
  );
  await idbWrite((store) => store.put(order));
  writeBackup(next);
  return next;
};

/**
 * Remove exactly the ids the SERVER confirmed, and nothing else.
 *
 * This is the POS's `socketRemoveOfflineOrders` rule and the whole reason a
 * partial batch is safe: an order the server rejected is not in the returned
 * documents, so it is never deleted and is still there for the next attempt.
 */
export const removeConfirmed = async (
  confirmedIds: string[],
): Promise<QueuedOrder[]> => {
  const ids = new Set(confirmedIds.filter(Boolean));
  const existing = await readAll();
  const next = existing.filter((row) => !ids.has(row._id));
  await idbWrite((store) => ids.forEach((id) => store.delete(id)));
  writeBackup(next);
  return next;
};

/** Record an attempt against rows that did NOT come back, for the UI. */
export const recordFailure = async (
  ids: string[],
  error: string | null,
): Promise<QueuedOrder[]> => {
  const wanted = new Set(ids.filter(Boolean));
  const existing = await readAll();
  const next = existing.map((row) =>
    wanted.has(row._id)
      ? { ...row, attempts: row.attempts + 1, last_error: error }
      : row,
  );
  await idbWrite((store) =>
    next.filter((row) => wanted.has(row._id)).forEach((row) => store.put(row)),
  );
  writeBackup(next);
  return next;
};
